import React from 'react';
import { formatRelativeTime } from '@/utils/formatting';

export interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  senderAvatar?: string;
  content: string;
  type: 'text' | 'product_share' | 'emoji' | 'system' | 'reaction';
  timestamp: Date;
  edited?: boolean;
  editedAt?: Date;
  replyTo?: string;
  metadata?: {
    productId?: string;
    productTitle?: string;
    productImage?: string;
    productPrice?: number;
    emoji?: string;
    reactionType?: string;
    targetMessageId?: string;
  };
  reactions?: ChatReaction[];
}

export interface ChatReaction {
  emoji: string;
  users: string[];
  count: number;
}

interface ChatMessageProps {
  message: ChatMessage;
  isCurrentUser: boolean;
  showAvatar?: boolean;
  onReact?: (messageId: string, emoji: string) => void;
  onReply?: (message: ChatMessage) => void;
  onEdit?: (messageId: string, newContent: string) => void;
  onDelete?: (messageId: string) => void;
  className?: string;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  isCurrentUser,
  showAvatar = true,
  onReact,
  onReply,
  onEdit,
  onDelete,
  className = '',
}) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [editContent, setEditContent] = React.useState(message.content);
  const [showReactions, setShowReactions] = React.useState(false);

  const handleEdit = () => {
    if (editContent.trim() && editContent !== message.content) {
      onEdit?.(message.id, editContent.trim());
    }
    setIsEditing(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleEdit();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditContent(message.content);
    }
  };

  const commonReactions = ['👍', '❤️', '😂', '🤔', '👎', '🎉'];

  const renderMessageContent = () => {
    switch (message.type) {
      case 'product_share':
        return (
          <div className="max-w-xs">
            <div className="bg-white rounded-lg border p-3 shadow-sm">
              {message.metadata?.productImage && (
                <img
                  src={message.metadata.productImage}
                  alt={message.metadata.productTitle}
                  className="w-full h-32 object-cover rounded-md mb-2"
                />
              )}
              <h4 className="font-semibold text-sm text-gray-900 mb-1">
                {message.metadata?.productTitle}
              </h4>
              {message.metadata?.productPrice && (
                <p className="text-green-600 font-bold text-sm">
                  ${message.metadata.productPrice.toFixed(2)}
                </p>
              )}
            </div>
            {message.content && (
              <div className="mt-2 text-sm">
                {message.content}
              </div>
            )}
          </div>
        );

      case 'emoji':
        return (
          <div className="text-4xl">
            {message.metadata?.emoji || message.content}
          </div>
        );

      case 'system':
        return (
          <div className="text-center text-gray-500 italic text-sm">
            {message.content}
          </div>
        );

      case 'reaction':
        return (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-lg">{message.metadata?.emoji}</span>
            <span>reacted to a message</span>
          </div>
        );

      default:
        return isEditing ? (
          <div className="w-full">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              onKeyDown={handleKeyPress}
              className="w-full p-2 border rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-2">
              <button
                onClick={() => {
                  setIsEditing(false);
                  setEditContent(message.content);
                }}
                className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleEdit}
                className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        ) : (
          <div className="break-words whitespace-pre-wrap">
            {message.content}
          </div>
        );
    }
  };

  if (message.type === 'system') {
    return (
      <div className={`flex justify-center my-4 ${className}`}>
        <div className="bg-gray-100 rounded-full px-4 py-2 max-w-md">
          {renderMessageContent()}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex gap-3 ${isCurrentUser ? 'flex-row-reverse' : 'flex-row'} ${className}`}>
      {/* Avatar */}
      {showAvatar && (
        <div className="flex-shrink-0">
          {message.senderAvatar ? (
            <img
              src={message.senderAvatar}
              alt={message.senderName}
              className="w-8 h-8 rounded-full"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-sm font-semibold">
              {message.senderName.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
      )}

      {/* Message Content */}
      <div className={`flex-1 max-w-xs ${isCurrentUser ? 'text-right' : 'text-left'}`}>
        {/* Sender Name & Time */}
        {!isCurrentUser && (
          <div className="text-xs text-gray-600 mb-1">
            {message.senderName}
          </div>
        )}

        {/* Message Bubble */}
        <div
          className={`relative inline-block p-3 rounded-2xl ${
            isCurrentUser
              ? 'bg-blue-600 text-white rounded-tr-md'
              : 'bg-gray-100 text-gray-900 rounded-tl-md'
          }`}
          onDoubleClick={() => isCurrentUser && message.type === 'text' && setIsEditing(true)}
        >
          {renderMessageContent()}

          {/* Message Actions */}
          <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full shadow-lg flex gap-1 p-1">
            {onReact && (
              <button
                onClick={() => setShowReactions(!showReactions)}
                className="p-1 hover:bg-gray-100 rounded-full text-xs"
                title="Add reaction"
              >
                😊
              </button>
            )}
            {onReply && (
              <button
                onClick={() => onReply(message)}
                className="p-1 hover:bg-gray-100 rounded-full text-xs"
                title="Reply"
              >
                ↩️
              </button>
            )}
            {isCurrentUser && message.type === 'text' && (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="p-1 hover:bg-gray-100 rounded-full text-xs"
                  title="Edit"
                >
                  ✏️
                </button>
                {onDelete && (
                  <button
                    onClick={() => onDelete(message.id)}
                    className="p-1 hover:bg-red-100 rounded-full text-xs text-red-600"
                    title="Delete"
                  >
                    🗑️
                  </button>
                )}
              </>
            )}
          </div>

          {/* Quick Reactions */}
          {showReactions && (
            <div className="absolute -bottom-12 left-0 bg-white rounded-lg shadow-lg p-2 flex gap-1 z-10">
              {commonReactions.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => {
                    onReact?.(message.id, emoji);
                    setShowReactions(false);
                  }}
                  className="p-1 hover:bg-gray-100 rounded text-lg"
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Message Reactions */}
        {message.reactions && message.reactions.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {message.reactions.map((reaction, index) => (
              <button
                key={index}
                onClick={() => onReact?.(message.id, reaction.emoji)}
                className="inline-flex items-center gap-1 bg-gray-100 hover:bg-gray-200 rounded-full px-2 py-1 text-xs transition-colors"
              >
                <span>{reaction.emoji}</span>
                <span className="text-gray-600">{reaction.count}</span>
              </button>
            ))}
          </div>
        )}

        {/* Timestamp & Edit Status */}
        <div className={`text-xs text-gray-500 mt-1 ${isCurrentUser ? 'text-right' : 'text-left'}`}>
          {formatRelativeTime(message.timestamp)}
          {message.edited && <span className="ml-1">(edited)</span>}
        </div>
      </div>
    </div>
  );
};